

import React, { useState } from "react";
import {
    Box,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import GroupOffOutlinedIcon from "@mui/icons-material/GroupOffOutlined";
import { FieldSelect } from "../../../../components/selections/fieldInputs";
import { QR, QrActionButton } from "../../../qrCode/components/qrUi";
import { useSelector } from "react-redux";
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import { addLevelEmployeesThunk } from "../../leadMangementThunk";
import { useDispatch } from "react-redux";
import { resetLeadManagementState } from "../../leadMangementSlice";



const InsertUnAssigningEmployees = ({ stateData, handleEmptyState, onInserted }) => {
    const dispatch = useDispatch();
    const { status3, getUnAssignedEmployees = [] } = useSelector((state) => state.leadManagement);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const unAssignedEmployess = getUnAssignedEmployees.map((item) => ({ value: item.employee_id, label: item.employee_name }));

    const { selectBranchDepartmentId, selectedLevelId, selectedHeadLevelEmpId } = stateData;

    const [state, setState] = useState({
        selectedUsers: [],
        empCards: [""],
        errors: {}
    });

    // ✅ Add new employee dropdown card
    const handleAddEmpCard = () => {
        if (state.empCards.length < unAssignedEmployess.length) {
            setState((prev) => ({
                ...prev,
                empCards: [...prev.empCards, ""],
            }));
        }
    };


    // ✅ Remove selected card
    const handleRemoveEmployee = (index) => {
        setState((prev) => {
            const newEmpCards = [...prev.empCards];
            const removedUser = newEmpCards[index];

            newEmpCards.splice(index, 1);

            const newSelectedUsers = prev.selectedUsers.filter(
                (userId) => userId !== removedUser
            );

            const newErrors = { ...prev.errors };
            delete newErrors[index];

            const finalCards = newEmpCards.length > 0 ? newEmpCards : [""];

            return {
                ...prev,
                empCards: finalCards,
                selectedUsers: newSelectedUsers,
                errors: newErrors,
            };
        });
    };


    // ✅ When a user is selected
    const handleUserChange = (index, userId) => {
        setState((prev) => {
            const newSelected = [...prev.selectedUsers];
            const newCards = [...prev.empCards];

            const oldUser = newCards[index];
            if (oldUser) {
                const i = newSelected.indexOf(oldUser);
                if (i !== -1) newSelected.splice(i, 1);
            }

            if (userId) newSelected.push(userId);
            newCards[index] = userId;

            return {
                ...prev,
                selectedUsers: newSelected,
                empCards: newCards,
            };
        });
    };

    // ✅ Available users (excluding already selected)
    const getAvailableUsers = (index) => {
        return unAssignedEmployess.filter(
            (u) =>
                !state.selectedUsers.includes(u.value) ||
                state.empCards[index] === u.value
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = {};
        state.empCards.forEach((card, index) => {
            if (!card) errors[index] = "Please select an employee";
        });

        if (Object.keys(errors).length > 0) {
            setState((prev) => ({ ...prev, errors }));
            setSnackbar({
                open: true,
                message: "Please select all employees before submitting",
                severity: "error",
            });
            return;
        }

        try {
            const result = await dispatch(
                addLevelEmployeesThunk({
                    credentials: {
                        "head_employee_id": selectedHeadLevelEmpId,
                        "branch_department_id": selectBranchDepartmentId,
                        "newEmployees": state.selectedUsers,
                        "level_id": selectedLevelId,
                    }
                })
            );

            if (result.meta.requestStatus === "fulfilled") {
                if (onInserted) {
                    // parent switches to the List tab, keeps the selected fields and shows the message
                    onInserted(result.payload?.message);
                    return;
                }
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });
                dispatch(resetLeadManagementState());
                handleEmptyState();
            } else {
                setSnackbar({
                    open: true,
                    message: result.payload,
                    severity: "error",
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }
    };

    const canAddMore = state.empCards.length < unAssignedEmployess.length;

    return (
        <>
            {
                unAssignedEmployess.length !== 0 ? (
                    <>
                        {/* Info banner */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1.25,
                                p: 1.25,
                                pl: 1.5,
                                borderRadius: 2.5,
                                bgcolor: `${QR.main}0d`,
                                border: `1px solid ${QR.main}33`,
                            }}
                        >
                            <Box sx={{ width: 32, height: 32, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", background: `linear-gradient(135deg, ${QR.main}, ${QR.light})` }}>
                                <PersonAddAlt1Icon sx={{ fontSize: 18 }} />
                            </Box>
                            <Box sx={{ mr: "auto" }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: QR.dark }}>
                                    <span style={{ color: "#ef4444" }}>*</span> Available Unassigned employees are {unAssignedEmployess.length}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                    Pick the employees to add to this level, one per box
                                </Typography>
                            </Box>
                            <Box component="span" sx={{ px: 1.25, py: 0.4, borderRadius: 5, fontSize: 12, fontWeight: 800, color: QR.main, bgcolor: "#fff", border: `1px solid ${QR.main}33` }}>
                                {state.selectedUsers.length} / {unAssignedEmployess.length} selected
                            </Box>
                        </Box>

                        {/* Employee pickers + add tile */}
                        <Box
                            sx={{
                                mt: 2,
                                display: "grid",
                                gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
                                gap: 1.25,
                            }}
                        >
                            {state.empCards.map((selected, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        position: "relative",
                                        px: 1.1,
                                        pt: 1,
                                        pb: 1.1,
                                        borderRadius: 2,
                                        bgcolor: "#fff",
                                        border: `1px solid ${state.errors[index] ? "#fecaca" : selected ? `${QR.main}55` : QR.border}`,
                                        boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                                    }}
                                >
                                    {/* number badge on the corner */}
                                    <Box component="span" sx={{ position: "absolute", top: -8, left: -8, width: 19, height: 19, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 800, color: selected ? "#fff" : "#64748b", bgcolor: selected ? QR.main : "#f1f5f9", border: "2px solid #fff", boxShadow: "0 1px 3px rgba(16,24,40,0.15)" }}>
                                        {index + 1}
                                    </Box>

                                    {/* 👇 Employee Dropdown + delete on the right */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-end",
                                            gap: 0.75,
                                            "& label.MuiTypography-root": { fontSize: 11, mb: 0.5 },
                                            "& .MuiOutlinedInput-root": { height: "36px !important", minHeight: "36px !important", fontSize: "13.5px !important", borderRadius: 2 },
                                            "& .MuiInputAdornment-root .MuiSvgIcon-root, & .MuiInputAdornment-root svg": { fontSize: "17px !important" },
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <FieldSelect
                                                name={`employee-${index}`}
                                                label="Select Employee"
                                                options={getAvailableUsers(index)}
                                                value={selected}
                                                onChange={(_, newValue) => handleUserChange(index, newValue || null)}
                                                error={!!state.errors[index]}
                                                icon={<PersonOutlineIcon />}
                                                placeholder="Search employee"
                                            />
                                        </Box>
                                        {/* ❌ Remove Card */}
                                        <Tooltip title={index === 0 ? "Clear Selection" : "Delete"}>
                                            <IconButton
                                                onClick={() => handleRemoveEmployee(index)}
                                                aria-label={index === 0 ? "clear selection" : "delete"}
                                                sx={{
                                                    flexShrink: 0,
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: 2,
                                                    color: "#dc2626",
                                                    bgcolor: "#fef2f2",
                                                    border: "1.5px solid #fecaca",
                                                    "&:hover": { color: "#fff", bgcolor: "#dc2626", borderColor: "#dc2626" },
                                                }}
                                            >
                                                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            ))}

                            {/* Add tile (same handler as the old Add button) */}
                            <Box
                                component="button"
                                type="button"
                                onClick={handleAddEmpCard}
                                title={canAddMore ? "Add another employee" : "All available employees already have a box"}
                                sx={{
                                    minHeight: 78,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.5,
                                    font: "inherit",
                                    cursor: canAddMore ? "pointer" : "not-allowed",
                                    borderRadius: 2,
                                    border: `1.5px dashed ${canAddMore ? "#93c5fd" : "#e2e8f0"}`,
                                    bgcolor: canAddMore ? "#f8fbff" : "#f8fafc",
                                    color: canAddMore ? QR.blue : "#cbd5e1",
                                    transition: "all .15s ease",
                                    "&:hover": canAddMore ? { bgcolor: "#eff6ff", borderColor: QR.blue } : {},
                                }}
                            >
                                <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
                                <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>Add</Typography>
                            </Box>
                        </Box>

                        {state.selectedUsers.length > 0 &&
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${QR.border}`,
                                    "& > *": { width: { xs: "100%", sm: "auto" } },
                                    "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
                                }}
                            >
                                <Box>
                                    <QrActionButton
                                        variant="solid"
                                        color={QR.blue}
                                        gradientTo="#3b82f6"
                                        icon={<CheckRoundedIcon sx={{ fontSize: 18 }} />}
                                        label="Submit"
                                        loading={status3 === "loading"}
                                        loadingLabel="Submitting..."
                                        onClick={handleSubmit}
                                    />
                                </Box>
                            </Box>}
                    </>
                ) : (
                    <Box sx={{ mt: 1, py: 4, textAlign: "center", borderRadius: 3, border: "1px dashed #cbd5e1", bgcolor: "#f8fafc" }}>
                        <GroupOffOutlinedIcon sx={{ color: "#cbd5e1", fontSize: 32 }} />
                        <Typography sx={{ mt: 0.5, fontSize: 13.5, fontWeight: 600, color: "#64748b" }}>No Data Found</Typography>
                        <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>There are no unassigned employees in this branch department.</Typography>
                    </Box>
                )
            }


            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </>
    );
};

export default InsertUnAssigningEmployees;

